import {
  Callout,
  DirectionalHint,
  FocusZone,
  FocusZoneDirection,
  Label,
  List,
  SearchBox,
  Stack,
  Text,
  mergeStyleSets,
} from "@fluentui/react";
import { Component, createRef } from "react";
import { v4 as uuidv4 } from "uuid";

export interface PwSearchableSelectOption {
  key: string | number;
  text: string;
  [key: string]: any;
}

interface PwSearchableSelectProps {
  label?: string;
  options: PwSearchableSelectOption[];
  selectedOption?: PwSearchableSelectOption | null;
  onChange?: (option: PwSearchableSelectOption) => void;
  disabled?: boolean;
  placeholder?: string;
}

interface PwSearchableSelectState {
  isOpen: boolean;
  searchText: string;
  filteredOptions: PwSearchableSelectOption[];
}

const styles = mergeStyleSets({
  container: {
    position: "relative",
  },
  inputWrapper: {
    border: "1px solid #605e5c",
    borderRadius: 2,
    backgroundColor: "#ffffff",
    selectors: {
      "&:focus-within": {
        borderColor: "#0078d4",
        borderWidth: 2,
      },
    },
  },
  inputWrapperDisabled: {
    border: "1px solid #c8c6c4",
    borderRadius: 2,
    backgroundColor: "#f3f2f1",
    pointerEvents: "none",
  },
  callout: {
    minWidth: 200,
    padding: 0,
  },
  listScrollContainer: {
    maxHeight: 260,
    overflowY: "auto",
  },
  optionList: {
    listStyleType: "none",
    margin: 0,
    padding: 0,
  },
  optionItem: {
    padding: "8px 12px",
    cursor: "pointer",
    selectors: {
      "&:hover": {
        backgroundColor: "#f3f2f1",
      },
      "&:focus": {
        backgroundColor: "#edebe9",
        outline: "none",
      },
    },
  },
  optionItemSelected: {
    backgroundColor: "#deecf9",
    selectors: {
      "&:hover": {
        backgroundColor: "#c7e0f4",
      },
    },
  },
  noResults: {
    padding: "8px 12px",
    color: "#605e5c",
    fontStyle: "italic",
  },
  selectedValue: {
    padding: "0 8px",
    fontSize: 14,
    color: "#323130",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 32,
    selectors: {
      "&:hover": {
        backgroundColor: "#f3f2f1",
      },
    },
  },
  clearIcon: {
    color: "#605e5c",
    fontSize: 12,
    cursor: "pointer",
    padding: "0 4px",
    selectors: {
      "&:hover": {
        color: "#201f1e",
      },
    },
  },
  searchBox: {
    border: "none",
    borderBottom: "1px solid #edebe9",
    borderRadius: 0,
  },
});

export default class PwSearchableSelect extends Component<
  PwSearchableSelectProps,
  PwSearchableSelectState
> {
  private containerRef = createRef<HTMLDivElement>();
  private listContainerRef = createRef<HTMLDivElement>();
  private listboxId = `pw-listbox-${uuidv4()}`;

  constructor(props: PwSearchableSelectProps) {
    super(props);
    this.state = {
      isOpen: false,
      searchText: "",
      filteredOptions: props.options,
    };
  }

  componentDidUpdate(prevProps: PwSearchableSelectProps) {
    if (prevProps.options !== this.props.options) {
      this.setState({
        filteredOptions: this.filterOptions(this.state.searchText, this.props.options),
      });
    }
  }

  normalize(text: string) {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  }

  filterOptions(text: string, options: PwSearchableSelectOption[]) {
    if (!text.trim()) return options;
    const needle = this.normalize(text);
    return options.filter((o) => this.normalize(o.text).includes(needle));
  }

  openDropdown() {
    if (this.props.disabled) return;
    this.setState({
      isOpen: true,
      searchText: "",
      filteredOptions: this.props.options,
    });
  }

  closeDropdown() {
    this.setState({ isOpen: false, searchText: "" });
  }

  selectOption(option: PwSearchableSelectOption) {
    this.closeDropdown();
    this.props.onChange?.(option);
  }

  clearSelection(e: React.MouseEvent) {
    e.stopPropagation();
    this.props.onChange?.(null as any);
  }

  onSearch(text?: string) {
    const value = text ?? "";
    this.setState({
      searchText: value,
      filteredOptions: this.filterOptions(value, this.props.options),
    });
  }

  renderOption = (option?: PwSearchableSelectOption) => {
    if (!option) return null;
    const isSelected = this.props.selectedOption?.key === option.key;
    return (
      <div
        key={option.key}
        className={`${styles.optionItem} ${isSelected ? styles.optionItemSelected : ""}`}
        onClick={() => this.selectOption(option)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") this.selectOption(option);
        }}
        role="option"
        aria-selected={isSelected}
        tabIndex={0}
        data-is-focusable
      >
        <Text variant="small">{option.text}</Text>
      </div>
    );
  };

  render() {
    const { label, selectedOption, disabled, placeholder = "Seleccione una opción..." } = this.props;
    const { isOpen, filteredOptions } = this.state;

    return (
      <div className={styles.container} ref={this.containerRef}>
        {label && <Label disabled={disabled}>{label}</Label>}

        <div
          className={disabled ? styles.inputWrapperDisabled : styles.inputWrapper}
          onClick={() => !isOpen && this.openDropdown()}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={this.listboxId}
          aria-label={label ?? placeholder}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") !isOpen && this.openDropdown(); }}
        >
          {!isOpen ? (
            <div className={styles.selectedValue}>
              <Text
                variant="small"
                style={{ color: selectedOption ? "#323130" : "#a19f9d" }}
              >
                {selectedOption ? selectedOption.text : placeholder}
              </Text>
              {selectedOption && !disabled && (
                <button
                  className={styles.clearIcon}
                  onClick={this.clearSelection.bind(this)}
                  aria-label="Limpiar selección"
                  type="button"
                >
                  ✕
                </button>
              )}
            </div>
          ) : null}
        </div>

        {isOpen && this.containerRef.current && (
          <Callout
            target={this.containerRef.current}
            directionalHint={DirectionalHint.bottomLeftEdge}
            isBeakVisible={false}
            onDismiss={() => this.closeDropdown()}
            calloutWidth={this.containerRef.current.offsetWidth}
            className={styles.callout}
          >
            <Stack>
              <SearchBox
                placeholder={placeholder}
                onChange={(_e, val) => this.onSearch(val)}
                onClear={() => this.onSearch("")}
                autoFocus
                className={styles.searchBox}
                underlined={false}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    const first = this.listContainerRef.current?.querySelector<HTMLElement>("[data-is-focusable]");
                    first?.focus();
                    e.preventDefault();
                  }
                }}
              />
              <div className={styles.listScrollContainer}>
                <FocusZone direction={FocusZoneDirection.vertical} isCircularNavigation>
                  <div role="listbox" id={this.listboxId} className={styles.optionList} ref={this.listContainerRef}>
                    {filteredOptions.length > 0 ? (
                      <List
                        items={filteredOptions}
                        onRenderCell={this.renderOption}
                      />
                    ) : (
                      <div className={styles.noResults}>
                        <Text variant="small">
                          No se encontraron resultados que coincidan con &ldquo;{this.state.searchText}&rdquo;.
                        </Text>
                      </div>
                    )}
                  </div>
                </FocusZone>
              </div>
            </Stack>
          </Callout>
        )}
      </div>
    );
  }
}
